import {
  ActionConfirmationModal,
  AppModal,
  Badge,
  Button,
  Form,
  Input,
  Label,
  LoadingSpinner,
  Typography,
} from "@/components/ui";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import {
  useCreateMatterTag,
  useDeleteMatterTag,
  useUpdateMatterTag,
} from "@/lib/data/react-query";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { type MatterTag } from "@/lib/types";
import {
  getMatterTagChipClassNames,
  getMatterTagSwatchClassNames,
  matterTagColorOptions,
} from "@/lib/utils/matters/matter-tag-color";
import { cn } from "@/lib/utils/cn";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { toLower } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

interface AddOrEditMatterTagModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  setActiveMatterTagId: (id: string) => void;
  editing: boolean;
}

type ConditionalProps =
  | {
      isReadOnly?: boolean;
      editing: true;
      activeMatterTag: MatterTag;
    }
  | {
      isReadOnly?: never;
      editing: false;
      activeMatterTag?: never;
    };

const MAX_MATTER_TAG_LABEL_LENGTH = 15;

const formSchema = z.object({
  label: z
    .string()
    .min(1, "Label is required")
    .max(
      MAX_MATTER_TAG_LABEL_LENGTH,
      `Label must be ${MAX_MATTER_TAG_LABEL_LENGTH} characters or fewer`,
    ),
  color: z.string().min(1, "Color is required"),
});

const defaultObject = {
  label: "",
  color: matterTagColorOptions[0].value,
};

const normalizeTagLabel = (value: string) => toLower(value.trim());

export function AddOrEditMatterTagModal({
  activeMatterTag,
  editing,
  isReadOnly,
  open,
  setActiveMatterTagId,
  setOpen,
}: AddOrEditMatterTagModalProps & ConditionalProps) {
  const { hasActionPermission } = useUser();
  const [isDeleting, setIsDeleting] = useState(false);
  const deleteMatterTag = useDeleteMatterTag();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    values: editing ? { ...defaultObject, ...activeMatterTag } : defaultObject,
    mode: "onSubmit",
  });

  useEffect(() => {
    if (!open) {
      form.reset();
    }
  }, [open, form]);

  const createMatterTag = useCreateMatterTag();
  const updateMatterTag = useUpdateMatterTag();
  const isFormLoading =
    createMatterTag.isPending ||
    updateMatterTag.isPending ||
    deleteMatterTag.isPending;
  const formId = editing ? "edit-matter-tag-form" : "create-matter-tag-form";
  const hasDirtyFields = Object.keys(form.formState.dirtyFields).length > 0;
  const isFormDirty = form.formState.isDirty || hasDirtyFields;

  const { data: matterSettingsData } = useQuery(
    retrieveMatterSettingsQueryOptions(),
  );

  const matterTags = useMemo(
    () => matterSettingsData?.data.tags ?? [],
    [matterSettingsData],
  );

  const watchedLabel = form.watch("label");
  const watchedColor = form.watch("color");

  useEffect(() => {
    if (!watchedLabel) {
      form.clearErrors("label");
      return;
    }

    const normalizedLabel = normalizeTagLabel(watchedLabel);
    const isDuplicate = matterTags.some(
      (matterTag) =>
        normalizeTagLabel(matterTag.label) === normalizedLabel &&
        matterTag.id !== activeMatterTag?.id,
    );

    if (isDuplicate) {
      form.setError("label", {
        message: "Matter tag label must be unique",
      });
    } else {
      form.clearErrors("label");
    }
  }, [activeMatterTag?.id, form, matterTags, watchedLabel]);

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    if (
      matterTags.some(
        (matterTag) =>
          normalizeTagLabel(matterTag.label) ===
            normalizeTagLabel(data.label) &&
          matterTag.id !== activeMatterTag?.id,
      )
    ) {
      form.setError("label", {
        message: "Matter tag label must be unique",
      });
      return;
    }

    if (editing) {
      await updateMatterTag.mutateAsync({
        id: activeMatterTag.id,
        dateModified: activeMatterTag.dateModified,
        ...data,
      });
    } else {
      setActiveMatterTagId("temp");
      await createMatterTag.mutateAsync(data).then((res) => {
        setActiveMatterTagId(res.data.id);
        form.reset();
      });
    }

    setOpen(false);
  };

  const handleDelete = async () => {
    setOpen(false);
    if (editing) {
      await deleteMatterTag
        .mutateAsync({
          id: activeMatterTag.id,
        })
        .then(() => {
          setIsDeleting(false);
        });
    }
  };

  return (
    <>
      <AppModal
        open={open}
        onOpenChange={setOpen}
        isDirty={isFormDirty}
        title={editing ? "Edit tag" : "Add tag"}
        description={
          editing
            ? "Update the tag name or color."
            : "Create a tag to organize matters."
        }
        size="xl"
        loading={isFormLoading}
        footer={
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            {!isReadOnly &&
            editing &&
            hasActionPermission(
              ACTION_PERMISSION_KEYS.matterTag,
              ACTION_PERMISSION_VALUES.delete,
            ) ? (
              <Button
                type="button"
                variant="destructive"
                className="sm:mr-auto"
                onClick={() => {
                  setIsDeleting(true);
                }}
              >
                Delete
              </Button>
            ) : (
              <div className="hidden sm:block sm:flex-1" />
            )}
            <div className="flex justify-end gap-2">
              <FormCancelButton
                label={isReadOnly ? "Close" : "Cancel"}
                setOpen={setOpen}
                isDirty={isFormDirty}
              />
              {!isReadOnly ? (
                <Button
                  type="submit"
                  form={formId}
                  disabled={isFormLoading || !isFormDirty}
                >
                  {isFormLoading ? <LoadingSpinner /> : "Save"}
                </Button>
              ) : null}
            </div>
          </div>
        }
      >
        <Form {...form}>
          <form
            id={formId}
            className="space-y-6"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <fieldset disabled={isReadOnly} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="matter-tag-label">Name</Label>
                <Input
                  id="matter-tag-label"
                  autoFocus
                  maxLength={MAX_MATTER_TAG_LABEL_LENGTH}
                  placeholder="Enter a tag name"
                  {...form.register("label")}
                />
                {form.formState.errors.label?.message ? (
                  <p className="text-destructive text-sm">
                    {form.formState.errors.label.message}
                  </p>
                ) : null}
              </div>

              <div className="space-y-3">
                <Label>Color</Label>
                <div className="flex flex-wrap gap-3">
                  {matterTagColorOptions.map((colorOption) => {
                    const isSelected = watchedColor === colorOption.value;
                    return (
                      <button
                        key={colorOption.value}
                        type="button"
                        aria-label={colorOption.label}
                        aria-pressed={isSelected}
                        className={cn(
                          "focus-visible:ring-ring flex h-10 w-10 items-center justify-center rounded-full border-2 transition-transform hover:scale-105 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden",
                          isSelected
                            ? "border-foreground scale-105"
                            : "border-transparent",
                        )}
                        onClick={() => {
                          form.setValue("color", colorOption.value, {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                        }}
                      >
                        <span
                          className={cn(
                            "block h-8 w-8 rounded-full",
                            getMatterTagSwatchClassNames(colorOption.value),
                          )}
                        />
                      </button>
                    );
                  })}
                </div>
                {form.formState.errors.color?.message ? (
                  <p className="text-destructive text-sm">
                    {form.formState.errors.color.message}
                  </p>
                ) : null}
              </div>

              <div className="space-y-2">
                <Typography className="text-sm font-medium">
                  Preview:
                </Typography>
                <Badge
                  className={cn(
                    "rounded-full border px-3 py-1 text-sm font-medium",
                    getMatterTagChipClassNames(watchedColor),
                  )}
                >
                  {watchedLabel.trim() || "Tag Preview"}
                </Badge>
              </div>
            </fieldset>
          </form>
        </Form>
      </AppModal>
      <ActionConfirmationModal
        title={`Are you sure you want to delete this matter tag?`}
        open={isDeleting && !!activeMatterTag}
        setOpen={setIsDeleting}
        afterActionFn={async () => {
          if (activeMatterTag) {
            await handleDelete();
          }
        }}
      />
    </>
  );
}
